import ApiService from './api';

/** A single intent match candidate returned by the NLP topic check. */
interface ScoredIntent {
  /** Technical intent name, e.g. `unknown`, `NAME`, or a real topic label */
  label: string;
  /** Match confidence score; accumulated when the same label occurs in multiple result sets */
  confidence: number;
  /** Additional backend fields (display name etc.) passed through untouched */
  [key: string]: any;
}

/** Payload for creating an intent relation. */
interface IntentRelationData {
  /** Technical name of the source intent */
  intent: string;
  /** Technical name of the intent it relates to */
  relatedIntent: string;
  /** Kind of relationship between the two intents */
  relation: 'similar' | 'special_case' | 'generalization';
}

/** A single intent match from the K2 index search. */
interface K2IndecyItem {
  /** The intent technical name */
  intent: string;
  /** The intent display name */
  displayName: string;
  /** The intent description */
  description: string;
  /** Alternative K2 names registered for the intent */
  k2names: string[];
  /** The confidence of the match / match score */
  score: number;
}

/** Result of a K2 index intent search. */
interface K2IndecyResult {
  /** Id of the bot */
  bot_id: string;
  /** The search query */
  text: string;
  /** Intents found */
  intents: K2IndecyItem[];
}

const IntentService = {
  async validateQuery(
    botId: string,
    query: string,
    title: string,
    description: string
  ): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/intents/assistant/validate-new-topic-query/${botId}`,
        {
          query,
          title,
          description,
        });

      return response.data;
    } catch (error) {
      return false;
    }
  },
  /**
   * @param botId Bot to search in
   * @param text Query text to compare against existing topics
   * @param language Language of the query, e.g. 'de' or 'en'
   */
  async similarIntents(
    botId: string,
    text: string,
    language: string = 'de'
  ): Promise<any> {
    const encodedText = encodeURIComponent(text);
    const response = await ApiService.get(
      `/schaltzentrale/intents/assistant/similar-topics/${botId}?query=${encodedText}&language=${language}`
    );

    return response.data;
  },
  /**
   * Runs the "new topic check" and returns intent candidates per backend
   * result group (e.g. 'own'), plus a merged `all` list across groups.
   */
  async searchIntent(
    botId: string,
    text: string
  ): Promise<Record<string, ScoredIntent[]>> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/nlp/newtopiccheck/${botId}`,
        { text }
      );
      let result: Record<string, ScoredIntent[]> = {};

      if (response && response.data && response.data.result) {
        for (const [key, value] of Object.entries(response.data.result) as [
          string,
          any,
        ][]) {
          let intents: ScoredIntent[] = value.topResults.slice();
          const best = intents.find(
            (intent) => intent.label === value.bestfit.label
          );
          if (best) {
            best.confidence += value.bestfit.confidence;
          } else {
            intents.push(value.bestfit);
          }

          intents = intents
            .filter((intent) => {
              if (
                intent.label === 'unknown' ||
                intent.label === 'NAME' ||
                intent.label === 'TOXIC' ||
                intent.label === 'NOISE' ||
                intent.confidence < 0.1
              ) {
                return false;
              }

              return true;
            })
            .sort((a, b) => b.confidence - a.confidence);

          result = { ...result, [key]: intents };
        }
      }

      // merge results except own
      const all: ScoredIntent[] = [];
      for (const [key, value] of Object.entries(result)) {
        if (key !== 'own') {
          // merge arrays
          for (const item of value) {
            // const existsInOwn = result.own.find(i => {
            //   return i.label === item.label
            // })

            // if (existsInOwn) {
            //   existsInOwn.confidence += item.confidence
            // } else {
            const exists = all.find((i) => i.label === item.label);
            if (!exists) {
              all.push(item);
            } else {
              exists.confidence += item.confidence;
            }
            // }
          }
        }
      }
      result.all = all;
      result.all.sort((a, b) => b.confidence - a.confidence);

      // 'own' is expected to always be present in a successful backend result
      result.own!.sort((a, b) => b.confidence - a.confidence);

      return result;
    } catch (error) {
      return {};
    }
  },
  /**
   * @param type Default-intent category segment of the endpoint URL
   * @param botId Bot to fetch defaults for
   */
  async getDefaults(type: string, botId: string): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/intent/${type}/${botId}`
      );
      return response.data;
    } catch (_) {
      return [];
    }
  },
  async saveRelation(botId: string, data: IntentRelationData): Promise<any> {
    try {
      if (!data?.intent || !data?.relation || !data?.relatedIntent) {
        return false;
      }

      const response = await ApiService.post(
        `/schaltzentrale/intent/${botId}/relation`,
        data
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async deleteRelation(
    botId: string,
    data: Pick<IntentRelationData, 'intent' | 'relatedIntent'>
  ): Promise<any> {
    try {
      if (!data?.intent || !data?.relatedIntent) {
        return false;
      }

      const response = await ApiService.delete(
        `/schaltzentrale/intent/${botId}/relation`,
        data
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },
  // Initialize a poll for the review assistant
  async initReviewAssist({
    uniqueBotId,
    intentInReview,
    excludeIntentsInReview,
  }: {
    /** Bot the review runs against */
    uniqueBotId: string;
    /** Technical name of the intent being reviewed */
    intentInReview: string;
    /** When true, other intents currently in review are excluded from the comparison */
    excludeIntentsInReview?: boolean;
  }): Promise<any> {
    excludeIntentsInReview = !!excludeIntentsInReview || false;
    try {
      const response = await ApiService.post(
        `/schaltzentrale/reviewAssistant/${uniqueBotId}/intentReview`,
        { uniqueBotId, intentInReview, excludeIntentsInReview }
      );
      return response.data;
    } catch (error) {
      return { error };
    }
  },

  // Check status of poll for the review assistant
  async checkReviewAssist({
    uniqueBotId,
    intentReviewId,
  }: {
    /** Bot the review runs against */
    uniqueBotId: string;
    /** Id of the poll created by initReviewAssist */
    intentReviewId: string;
  }): Promise<any> {
    console.log('getReviewAssist');
    try {
      const response = await ApiService.get(
        `/schaltzentrale/reviewAssistant/${uniqueBotId}/intentReview/${intentReviewId}`);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  // All props in lower case, please.
  async saveK2Names({
    botId,
    k2names,
    intent,
  }: {
    /** Bot the intent belongs to */
    botId: string;
    /** Alternative K2 names to store for the intent */
    k2names: string[];
    /** Technical name of the intent */
    intent: string;
  }): Promise<true | undefined> {
    try {
      // NOTE: resolves undefined on success and true on error — pre-existing quirk kept as-is
      await ApiService.post(`/schaltzentrale/intent/${botId}/k2names`, {
        k2names,
        intent,
      });
    } catch (error) {
      return true;
    }
  },

  /**
   * Searches for intents/topics using `k2` and `k2index`.
   * Both of these features must be enabled for the bot.
   * @param botId Current bot ID
   * @param text Search query
   * @param limit Optional: limit results to first N matches
   * @returns `false` if an error occurred, otherwise the result
   */
  async getK2Indices({
    botId,
    text,
    limit,
  }: {
    botId: string;
    text: string;
    limit?: number | null;
  }): Promise<false | K2IndecyResult> {
    const payload: { text: string; top_k?: number } = {
      text,
    };

    if (limit) {
      payload.top_k = limit;
    }

    try {
      const response = await ApiService.post(
        `/schaltzentrale/k2/index/intentreduce/${botId}`,
        payload
      );
      return response.data;
    } catch (error) {
      return false;
    }
  },

  /**
   * Based on an example query or a purpose description,
   * suggest an agent title, description, and typical user queries for a new AI Agent (RAGTopic).
   *
   * @param botId Bot the agent will be created for
   * @param text The user query
   * @param language The user's language (defaults to 'de')
   * @param similarityCheck Perform a similarity check among your intents against the suggested agent
   */
  async suggestNewAgent({
    botId,
    text,
    language,
    similarityCheck,
  }: {
    botId: string;
    text: string;
    language?: string | null;
    similarityCheck?: boolean | null;
  }): Promise<any> {
    const payload = {
      query: text,
      language: language || 'de',
      similarityChecks: !!similarityCheck,
    };

    try {
      const response = await ApiService.post(
        `/schaltzentrale/agents/creator/generate/${botId}`,
        payload
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
      };
    }
  },
};

export default IntentService;

export { IntentService };
